import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon, Loader2, Search } from "lucide-react";
import { useGetHistoryConsultationQuery } from "@/services/consultationApi";
import { HistoryConsultation } from "@/types/consultation";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { cn } from "@/lib/utils";

interface HistoryFilterProps {
  memberId: number;
  onDataFound: (data: HistoryConsultation | null) => void;
  className?: string;
}

const months = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 10 }, (_, i) => (currentYear - i).toString());

export const HistoryFilter: React.FC<HistoryFilterProps> = ({
  memberId,
  onDataFound,
  className,
}) => {
  const [month, setMonth] = useState<string>("");
  const [year, setYear] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 1;

  const { data: historyResponse, isFetching, isError } = useGetHistoryConsultationQuery(
    { memberId, month, year, orgId },
    { skip: !memberId || !month || !year }
  );

  useEffect(() => {
    if (historyResponse) {
      if (historyResponse.data && historyResponse.data.length > 0) {
        onDataFound(historyResponse.data[0]);
      } else {
        onDataFound(null);
      }
    }
  }, [historyResponse, onDataFound]);

  useEffect(() => {
     if (isError) {
        onDataFound(null);
     }
  }, [isError, onDataFound])

  const selectedMonthLabel = months.find((m) => m.value === month)?.label;

  return (
    <Card className={cn("p-4 border-[#e2e8f0] bg-[#f8fafc]", className)}>
      <div className="flex flex-col space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-sm text-[#1e293b]">See previous consultation</p>
            <p className="text-xs text-[#64748b] mt-0.5">
              View records from previous sessions
            </p>
          </div>
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-9 px-3 border-[#cbd5e1] hover:bg-white text-[#475569] font-medium"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {month && year ? `${selectedMonthLabel} ${year}` : "Select Period"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3" align="end">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">
                    Select Month
                  </label>
                  <Select value={month} onValueChange={setMonth}>
                    <SelectTrigger className="h-9 focus:ring-0">
                      <SelectValue placeholder="Month" />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">
                    Select Year
                  </label>
                  <Select value={year} onValueChange={setYear}>
                    <SelectTrigger className="h-9 focus:ring-0">
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={y}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  className="w-full h-9 bg-[#6be27f] hover:bg-[#49DE62] text-white font-medium" 
                  onClick={() => setIsOpen(false)}
                  disabled={!month || !year}
                >
                  <Search className="mr-2 h-4 w-4" />
                  Find History
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {isFetching && (
          <div className="flex items-center justify-center py-2 text-[#64748b] text-xs font-medium">
            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
            Searching for consultation records...
          </div>
        )}

        {month && year && !isFetching && !historyResponse?.data?.length && (
          <p className="text-[11px] text-[#ef4444] font-medium italic mt-1 bg-red-50 p-1.5 rounded border border-red-100 flex items-center">
             No records found for {selectedMonthLabel} {year}
          </p>
        )}
      </div>
    </Card>
  );
};
